import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { formatDraftAmount } from '../lib/draft';

export interface SummaryLine {
  label: string;
  amount: number;
}

interface SummaryCardProps {
  lines: SummaryLine[];
  totalLabel: string;
  totalAmount: number;
  footer?: ReactNode;
}

export const SectionOverline = ({ children }: { children: ReactNode }) => (
  <p className='text-h-text-medium text-[12.5px] font-semibold'>
    {children}
  </p>
);

export const InvoiceCard = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      'border-h-neutral-90 rounded-xl border bg-white p-4 shadow-[0_1px_3px_rgba(15,23,42,0.04)]',
      className,
    )}>
    {children}
  </div>
);

/**
 * Sustituye a la vista previa renderizada durante la captura. En el flujo
 * actual el preview recibe clicks sobre el receptor, los importes y hasta el
 * disclaimer legal —es donde la gente mira— pero nada de eso abre la edición.
 * Aquí solo viven importes; el documento editable aparece en el paso 3.
 */
export const SummaryCard = ({ lines, totalLabel, totalAmount, footer }: SummaryCardProps) => (
  <InvoiceCard>
    <SectionOverline>Importes</SectionOverline>
    <div className='mt-3 flex flex-col gap-2'>
      {lines.map((line, index) => (
        <div key={index} className='flex items-baseline justify-between gap-3 text-[13px]'>
          <span className='text-h-text-medium min-w-0 truncate'>{line.label}</span>
          <span className='text-h-text-heading flex-none font-mono tabular-nums'>
            {formatDraftAmount(line.amount)}
          </span>
        </div>
      ))}
      <div className='bg-h-neutral-90 my-1 h-px' />
      <div className='flex items-baseline justify-between gap-3'>
        <span className='text-h-text-heading text-[13px] font-semibold'>{totalLabel}</span>
        <span className='text-h-text-heading font-mono text-[17px] font-semibold tabular-nums'>
          {formatDraftAmount(totalAmount)}
        </span>
      </div>
    </div>
    {footer && <div className='mt-3'>{footer}</div>}
  </InvoiceCard>
);
